import { Collapse, Stack } from "@mui/material";
import { Product, ProductPrice } from "src/api/types";
import BaseCreateOrderForm from "../../../BaseCreateOrderForm";
import { useCallback, useMemo, useState } from "react";
import DatePicker from "src/components/DatePicker";
import { addMonths } from "date-fns";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useCreateDraftPrescriptionMutation } from "src/api/services/prescriptionsApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useExtractForm } from "src/hooks/useExtractForm";
import { useGetDaysSupply } from "src/hooks/useGetDaysSupply";
import { useDeterminePrescriptionType } from "src/hooks/useDeterminePrescriptionType";

interface Props {
  patientId: number;
  patientStripeId: string;
  gender: string | null;
  setIsError: (v: boolean) => void;
  setMessage: (v: string) => void;
}

const CreateOrderLaterTab = ({
  patientId,
  setIsError,
  setMessage,
  patientStripeId,
  gender,
}: Props) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<ProductPrice | null>(null);
  const [quantity, setQuantity] = useState<number | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [date, setDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [createDraftPrescription] = useCreateDraftPrescriptionMutation();
  const errorLog = useSentryLogger("error");
  const form = useExtractForm(selectedPrice, selectedProduct);
  const daysSupply = useGetDaysSupply(selectedProduct, selectedPrice);
  const prescriptionType = useDeterminePrescriptionType(
    selectedProduct?.id,
    patientId,
  );

  const maxDate = useMemo(() => addMonths(new Date(), 1), []);

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    if (selectedPrice == null || selectedProduct == null || date == null)
      return;

    try {
      await createDraftPrescription({
        patient_id: patientId,
        price_id: selectedPrice.id,
        product_id: selectedProduct.id,
        product_category_id: selectedProduct.category.id,
        due_at: date.toISOString(),
        dispense_quantity: quantity ? quantity.toString() : undefined,
        days_supply: daysSupply,
        type: prescriptionType,
        metadata: {
          form: form || undefined,
          price_id: selectedPrice.stripe_id || undefined,
          payment_method: paymentMethod || undefined,
          due_date: date.toISOString() || undefined,
        },
      }).unwrap();

      setMessage(`Draft prescription created for ${date}`);
    } catch (err) {
      setIsError(true);
      setMessage("Unable to create future order");
      errorLog("Error creating future one time order", err);
    } finally {
      setLoading(false);
    }
  }, [
    createDraftPrescription,
    date,
    daysSupply,
    errorLog,
    form,
    patientId,
    paymentMethod,
    prescriptionType,
    quantity,
    selectedPrice,
    selectedProduct,
    setIsError,
    setMessage,
  ]);

  return (
    <Stack gap={2}>
      <BaseCreateOrderForm
        patientId={patientId}
        quantity={quantity}
        gender={gender}
        selectedPrice={selectedPrice}
        selectedProduct={selectedProduct}
        setQuantity={setQuantity}
        setSelectedPrice={setSelectedPrice}
        setSelectedProduct={setSelectedProduct}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        patientStripeId={patientStripeId}
      />
      <Collapse in={!!selectedPrice} unmountOnExit>
        <DatePicker
          minDate={new Date()}
          maxDate={maxDate}
          value={date}
          onChange={setDate}
        />
      </Collapse>

      <LoadingButton disabled={!date} loading={loading} onClick={handleSubmit}>
        Submit
      </LoadingButton>
    </Stack>
  );
};

export default CreateOrderLaterTab;
